import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { publishAssessment } from '../store/slices/GoalSlice';
import { RootState } from '../store/store';
import useApi from './useApi';
import API from '../services/rest/api';
import { Assessment } from '../types/goal';
import { toast } from 'react-toastify';
import { useScoreProgress } from './useScoreProgress';
import { useCurrentPatientId, useCurrentPatientInfo } from './useCurrentPatient';

interface UseAssessmentPublisherProps {
  activeTab: string;
  onPublishSuccess?: () => void;
  onPublishError?: (message: string) => void;
  patientId: string;
  isPrimary: boolean;
  /**
   * Diagnostics only: true after a lab report is uploaded/applied in this session.
   * Publish is gated in the UI until this is true (or assessment is already preview).
   */
  diagnosticsReportApplied?: boolean;
}

export const useAssessmentPublisher = ({
  activeTab,
  patientId,
  onPublishSuccess,
  onPublishError,
  isPrimary,
  diagnosticsReportApplied = false,
}: UseAssessmentPublisherProps) => {
  const dispatch = useDispatch();
  const { goals, assessments } = useSelector(
    (state: RootState) =>
      state.goals.data[isPrimary ? "mainGoal" : "customGoals"]
  );
  const fallbackPatientId = useCurrentPatientId();
  const patientInfo = useCurrentPatientInfo();
  const resolvedPatientId = patientId || fallbackPatientId;

  const { getScoringHistoryData } = useScoreProgress();

  const {
    mutate: publishReviewedAssessment,
    response: publishReviewedAssessmentResponse,
    error: publishReviewedAssessmentError,
    isLoading: isPublishReviewedAssessmentLoading,
  } = useApi(API.publishAssessment);

  const parseAssessmentNumber = (id: string): number => {
    const match = id.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const getPreviousAssessmentId = (newAssessmentId: string): string | undefined =>
    Object.values(assessments)
      .filter(
        (assessment) =>
          assessment.id !== newAssessmentId &&
          !assessment.isNew &&
          assessment.action?.toLowerCase() !== "skipped" &&
          !assessment.isSkip
      )
      .sort(
        (a, b) => parseAssessmentNumber(b.id) - parseAssessmentNumber(a.id)
      )[0]?.id;

  const getPreviousValueFromSubGoal = (
    subGoal: { [key: string]: unknown },
    newAssessmentId: string,
    previousAssessmentId?: string
  ): string => {
    if (previousAssessmentId) {
      const fromPrevious =
        (subGoal[previousAssessmentId] as Assessment | undefined)?.value ?? "";
      if (fromPrevious !== "") return fromPrevious;
    }

    const assessmentKeys = Object.keys(subGoal)
      .filter(
        (key) =>
          key.includes("assessment") &&
          key !== newAssessmentId &&
          Boolean((subGoal[key] as Assessment | undefined)?.value)
      )
      .sort((a, b) => parseAssessmentNumber(b) - parseAssessmentNumber(a));

    if (!assessmentKeys.length) return "";
    return (subGoal[assessmentKeys[0]] as Assessment | undefined)?.value ?? "";
  };

  const buildFormattedData = (newAssessment: Assessment) => {
    const isDiagnostics = activeTab === "diagnostics";
    const previousAssessmentId = isDiagnostics
      ? getPreviousAssessmentId(newAssessment.id)
      : undefined;
    const diagnosticsValuesFromReport =
      diagnosticsReportApplied || newAssessment.action === "preview";

    return Object.values(goals)
      .map((goal) => {
        // Diet / exercise / success: keep original shape (all sub-goals + delta)
        if (!isDiagnostics) {
          const baseGoalData = {
            goal_id: goal.id,
            sub_goals:
              goal.subGoals?.map((subGoal) => ({
                id: subGoal.id,
                name: subGoal.name,
                value: (subGoal[newAssessment.id] as Assessment)?.value || "",
                delta: {
                  type:
                    (subGoal[newAssessment.id] as Assessment)?.delta?.type || "",
                },
              })) || [],
          };

          return Number(goal?.goalScore?.score ?? 0) > 0
            ? { ...baseGoalData, score: goal.goalScore!.score! }
            : baseGoalData;
        }

        // Diagnostics: omit empty values (API rejects them).
        // hasChanged only when values come from an uploaded/analysed report.
        const subGoals =
          goal.subGoals
            ?.map((subGoal) => {
              const cellValue =
                (subGoal[newAssessment.id] as Assessment | undefined)?.value ??
                "";
              const previousValue = getPreviousValueFromSubGoal(
                subGoal,
                newAssessment.id,
                previousAssessmentId
              );
              const value = cellValue !== "" ? cellValue : previousValue;
              if (value === "") return null;

              return {
                id: subGoal.id,
                name: subGoal.name,
                value,
                hasChanged: Boolean(
                  diagnosticsValuesFromReport && cellValue !== ""
                ),
              };
            })
            .filter((subGoal): subGoal is NonNullable<typeof subGoal> =>
              Boolean(subGoal)
            ) || [];

        if (subGoals.length === 0) return null;

        const baseGoalData = {
          goal_id: goal.id,
          sub_goals: subGoals,
        };

        return Number(goal?.goalScore?.score ?? 0) > 0
          ? { ...baseGoalData, score: goal.goalScore!.score! }
          : baseGoalData;
      })
      .filter((goal): goal is NonNullable<typeof goal> => Boolean(goal));
  };
  
  const postAssessment = async () => {
    try {
      let newAssessment = Object.values(assessments).find(
        (assessment) =>
          assessment.name === "New Assessment" && assessment.isNew
      );

      // Diagnostics-only: allow isNew assessments that may not use the default name
      if (!newAssessment && activeTab === "diagnostics") {
        newAssessment = Object.values(assessments).find(
          (assessment) => assessment.isNew
        );
      }

      if (!newAssessment) {
        if (activeTab === "diagnostics") {
          toast.error("No new assessment found to publish");
        }
        return;
      }

      if (!resolvedPatientId) {
        toast.error("Patient ID is missing. Please reload the patient page.");
        return;
      }

      const formattedData = buildFormattedData(newAssessment);

      if (activeTab === "diagnostics" && formattedData.length === 0) {
        toast.error(
          "No diagnostic values to publish. Please upload a report or wait for analysis."
        );
        return;
      }

      const assessment = {
        id: Number(newAssessment.id.split("_")[1]),
        data: formattedData,
      };

      const payload = {
        tag: activeTab,
        action: newAssessment?.isSkip ? "skipped" : "published",
        assessment,
        patient_id: resolvedPatientId,
        is_primary: isPrimary ? 1 : 0,
        disease: patientInfo?.disease || "",
      };
      await publishReviewedAssessment(payload);
      dispatch(
        publishAssessment({
          isPrimary,
          activeTab,
          patientId: resolvedPatientId,
        })
      );
    } catch (error) {
      console.error('Error publishing assessment:', error);
      onPublishError?.(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  useEffect(() => {
    if (publishReviewedAssessmentResponse?.code?.toString() === "1") {
      toast.success(publishReviewedAssessmentResponse?.message || "Assessment published successfully");
      onPublishSuccess?.();
      getScoringHistoryData();
    } else if (publishReviewedAssessmentResponse?.code?.toString() == "0" || publishReviewedAssessmentError) {
      toast.error(publishReviewedAssessmentResponse?.message || publishReviewedAssessmentError?.message || "Failed to publish assessment");
      onPublishError?.('Failed to publish assessment');
    }
  }, [publishReviewedAssessmentResponse, publishReviewedAssessmentError]);

  const handlePublish = () => {
    postAssessment();
  };

  return {
    handlePublish,
    isPublishing: isPublishReviewedAssessmentLoading,
  };
};
